import React from 'react';
import { Calendar } from 'lucide-react';
import { formatSimpleDate } from '../utils/dateFormatter';

const ProblemCard = ({ problem, onClick }) => {
  const getDomainStyle = (domain) => {
    const d = domain?.toLowerCase() || '';
    switch (d) {
      case 'electronics': return 'border border-blue-400 text-blue-400 bg-blue-400/10';
      case 'mechanical': return 'border border-purple-400 text-purple-400 bg-purple-400/10';
      case 'software': return 'border border-green-400 text-green-400 bg-green-400/10';
      case 'management': return 'border border-orange-400 text-orange-400 bg-orange-400/10';
      default: return 'border border-gray-400 text-gray-400 bg-gray-400/10';
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Not Decided': return 'border border-gray-400 text-gray-400 bg-gray-400/10';
      case 'In Progress': return 'border border-blue-400 text-blue-400 bg-blue-400/10';
      case 'Done': return 'border border-green-400 text-green-400 bg-green-400/10';
      default: return 'border border-gray-400 text-gray-400 bg-gray-400/10';
    }
  };

  const getPriorityStyle = (priority) => {
    const p = priority?.toLowerCase() || '';
    switch (p) {
      case 'critical': return 'border border-red-400 text-red-400 bg-red-400/10';
      case 'high': return 'border border-orange-400 text-orange-400 bg-orange-400/10';
      case 'medium': return 'border border-yellow-400 text-yellow-400 bg-yellow-400/10';
      case 'low': return 'border border-green-400 text-green-400 bg-green-400/10';
      default: return 'border border-gray-400 text-gray-400 bg-gray-400/10';
    }
  };

  const formatStatus = (status) => {
    return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };



  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    return due < today;
  };

  // Handle both old localStorage format and new Supabase format
  const getReportedDate = () => {
    return problem.date_reported || problem.dateReported || problem.created_at || '';
  };

  const getDueDate = () => {
    return problem.due_date || problem.dueDate || '';
  };

  return (
    <div
      onClick={onClick}
      className="glass-card p-6 cursor-pointer"
      data-testid={`problem-card-${problem.id}`}
    >
      {/* Badges */}
      <div className="flex flex-wrap gap-2 mb-4">
        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getDomainStyle(problem.domain)}`} data-testid="problem-domain">
          {problem.domain?.charAt(0).toUpperCase() + problem.domain?.slice(1)}
        </span>
        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusStyle(problem.status)}`} data-testid="problem-status">
          {formatStatus(problem.status)}
        </span>
        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPriorityStyle(problem.priority)}`} data-testid="problem-priority">
          {problem.priority?.charAt(0).toUpperCase() + problem.priority?.slice(1)}
        </span>
      </div>

      {/* Title */}
      <h3 className="text-lg font-semibold mb-3 line-clamp-2" data-testid="problem-title">
        {problem.title}
      </h3>

      {/* Description */}
      <p className="text-gray-400 text-sm mb-4 line-clamp-3" data-testid="problem-description">
        {problem.description}
      </p>

      {/* Footer */}
      <div className="flex items-center justify-between text-sm text-gray-400">
        <div className="flex items-center gap-2">
          <Calendar size={14} />
          <span data-testid="problem-date" className="text-xs">
            {formatSimpleDate(getReportedDate())}
          </span>
        </div>
        {getDueDate() && (
          <div className={`flex items-center gap-1 text-xs ${isOverdue(getDueDate()) ? 'text-red-400' : 'text-gray-400'}`} data-testid="problem-due-date">
            📅 Due: {formatSimpleDate(getDueDate())}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProblemCard;
